import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { track } from "../../lib/analytics";
import { Loader2, LogOut, Plus, Search } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@v2/components/ui/tabs";
import { supabaseV2 } from "../../lib/supabase";
import { eaPlatformLabel } from "../../lib/labels";
import { FcIcon } from "../../components/brand";
import { TeamCrest } from "../../components/primitives";
import { AuthShell } from "../../components/shell/AuthShell";

function buildSlugFromName(name: string): string {
  const base = (name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  const suffix = Math.random().toString(36).slice(2, 7);
  return base ? `${base}-${suffix}` : `team-${suffix}`;
}

export function Onboarding({ userEmail, onDone, onSignOut }: { userEmail: string; onDone: () => void; onSignOut: () => void }) {
  const { t } = useTranslation("auth");
  const [teamName, setTeamName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string | null>(null);
  const [joinQuery, setJoinQuery] = useState("");
  const [joinResults, setJoinResults] = useState<
    Array<{ id: string; name: string; slug: string; abbreviation: string | null; primary_color: string | null; secondary_color: string | null }>
  >([]);
  const [joinNote, setJoinNote] = useState("");
  const [pendingRequest, setPendingRequest] = useState<{ id: string; team_id: string } | null>(null);

  const [eaQuery, setEaQuery] = useState("");
  const [eaSearching, setEaSearching] = useState(false);
  const [eaError, setEaError] = useState<string | null>(null);
  const [eaResults, setEaResults] = useState<Array<{ clubId: string; name: string; platform: string; currentDivision?: string | null; gamesPlayed?: number | null; alreadyTaken?: boolean }>>([]);
  const [eaPicked, setEaPicked] = useState<{ clubId: string; name: string; platform: string } | null>(null);

  useEffect(() => {
    supabaseV2
      .from("team_join_requests")
      .select("id, team_id")
      .eq("status", "pending")
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setPendingRequest((data as any) || null));
  }, []);

  useEffect(() => {
    const trimmed = joinQuery.trim();
    if (trimmed.length < 2) {
      setJoinResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      const { data } = await supabaseV2.rpc("search_public_teams", { _q: trimmed });
      setJoinResults((data || []) as any);
    }, 250);
    return () => clearTimeout(handle);
  }, [joinQuery]);

  const requestJoin = async (teamId: string) => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabaseV2.rpc("request_to_join_team", {
      _team_id: teamId,
      _note: joinNote.trim() || null,
    });
    setLoading(false);
    if (error) setError(error.message);
    else setPendingRequest({ id: (data as any).id, team_id: (data as any).team_id });
  };

  const cancelJoin = async () => {
    if (!pendingRequest) return;
    setLoading(true);
    const { error } = await supabaseV2.rpc("cancel_join_request", { _request_id: pendingRequest.id });
    setLoading(false);
    if (error) setError(error.message);
    else setPendingRequest(null);
  };

  const createTeam = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!teamName.trim()) {
      setError(t("onboarding.create.missingTeamName"));
      return;
    }
    setLoading(true);
    setError(null);
    setLoadingStep(t("onboarding.create.steps.checkEa"));
    if (eaPicked) {
      const { data: taken } = await supabaseV2.rpc("is_ea_club_taken", {
        _ea_club_id: eaPicked.clubId,
        _ea_platform: eaPicked.platform,
      });
      if (taken === true) {
        setLoading(false);
        setLoadingStep(null);
        setError(
          t("onboarding.create.eaAlreadyTakenError", { name: eaPicked.name, clubId: eaPicked.clubId }),
        );
        return;
      }
    }
    setLoadingStep(t("onboarding.create.steps.createTeam"));
    let attempt = 0;
    let lastError: any = null;
    let data: any = null;
    while (attempt < 3) {
      const slug = buildSlugFromName(teamName);
      const result = await supabaseV2.rpc("create_team_with_owner", {
        _name: teamName.trim(),
        _slug: slug,
        _abbreviation: teamName.trim().slice(0, 3).toUpperCase(),
        _primary_color: "#005F7E",
        _secondary_color: "#004272",
      });
      if (!result.error) {
        data = result.data;
        lastError = null;
        break;
      }
      lastError = result.error;
      if ((result.error as any).code !== "23505") break;
      attempt += 1;
    }
    if (lastError) {
      setLoading(false);
      setLoadingStep(null);
      setError(lastError.message);
      return;
    }
    const newTeam: any = data;
    if (eaPicked && newTeam?.id) {
      setLoadingStep(t("onboarding.create.steps.linkEa"));
      const { error: updErr } = await supabaseV2
        .from("teams")
        .update({ ea_club_id: eaPicked.clubId, ea_platform: eaPicked.platform })
        .eq("id", newTeam.id);
      if (updErr) {
        setLoading(false);
        setLoadingStep(null);
        setError(`${t("onboarding.create.eaLinkErrorPrefix")} ${updErr.message}`);
        return;
      }
      try {
        setLoadingStep(t("onboarding.create.steps.syncEa"));
        await supabaseV2.functions.invoke("sync-ea-club", {
          body: { teamId: newTeam.id },
        });
      } catch (e) {
        console.warn("[onboarding] EA sync fallito:", e);
      }
      try {
        setLoadingStep(t("onboarding.create.steps.importRoster"));
        const { data: members } = await supabaseV2.rpc("list_ea_members", {
          _team_id: newTeam.id,
        });
        const memberRows = (members || []) as Array<{
          ea_persona: string;
          ea_name: string | null;
        }>;
        if (memberRows.length > 0) {
          const { data: existing } = await supabaseV2
            .from("players")
            .select("ea_account_handle")
            .eq("team_id", newTeam.id);
          const existingHandles = new Set(
            ((existing || []) as Array<{ ea_account_handle: string | null }>)
              .map((p) => (p.ea_account_handle || "").trim())
              .filter(Boolean),
          );
          const rosterPayload = memberRows
            .filter((m) => m.ea_persona && !existingHandles.has(m.ea_persona))
            .map((m) => ({
              team_id: newTeam.id,
              first_name: ((m.ea_name || m.ea_persona) ?? "").trim() || m.ea_persona,
              last_name: "",
              ea_account_handle: m.ea_persona,
              ea_sports_id: m.ea_persona,
              status: "active" as const,
              is_guest: false,
            }));
          if (rosterPayload.length > 0) {
            const { error: insertErr } = await supabaseV2
              .from("players")
              .insert(rosterPayload);
            if (insertErr) {
              console.warn("[onboarding] roster import fallito:", insertErr);
            }
          }
        }
      } catch (e) {
        console.warn("[onboarding] roster import fallito:", e);
      }
    }
    setLoadingStep(t("onboarding.create.steps.almostReady"));
    setLoading(false);
    setLoadingStep(null);
    // GA4 event "team_created" — conversione di attivazione founder
    // (signup → primo team). has_ea_club = true se collegato a Mondo FC/FC World/FC Welt.
    if (newTeam?.id) {
      track.teamCreated({ team_id: newTeam.id, has_ea_club: Boolean(eaPicked) });
    }
    onDone();
  };

  const runEaSearchOnboarding = async () => {
    if (!eaQuery.trim() || eaQuery.trim().length < 2) return;
    setEaSearching(true);
    setEaError(null);
    setEaResults([]);
    try {
      const { data: sess } = await supabaseV2.auth.getSession();
      if (!sess?.session?.access_token) {
        throw new Error(t("onboarding.create.sessionExpired"));
      }
      const { data, error: invokeError } = await supabaseV2.functions.invoke("search-ea-clubs", {
        body: { name: eaQuery.trim() },
      });
      if (invokeError) {
        let bodyMsg = "";
        try {
          const ctx: any = (invokeError as any).context;
          if (ctx?.body) {
            const txt = typeof ctx.body === "string" ? ctx.body : await ctx.body.text?.();
            if (txt) {
              try {
                const j = JSON.parse(txt);
                bodyMsg = j.error || j.message || txt;
              } catch {
                bodyMsg = txt;
              }
            }
          }
        } catch {
          // ignore
        }
        throw new Error(bodyMsg || invokeError.message || t("onboarding.create.searchError"));
      }
      const r = data as any;
      if (r?.error) throw new Error(r.error);
      const clubs = (r?.clubs || []) as any[];
      const takenSet = new Set<string>();
      if (clubs.length > 0) {
        const pairs = clubs.map((c) => ({ clubId: c.clubId, platform: c.platform }));
        const { data: takenRows } = await supabaseV2.rpc("taken_ea_clubs", { _pairs: pairs });
        for (const row of (takenRows || []) as any[]) {
          takenSet.add(`${row.ea_platform}:${row.ea_club_id}`);
        }
      }
      const annotated = clubs.map((c) => ({
        ...c,
        alreadyTaken: takenSet.has(`${c.platform}:${c.clubId}`),
      }));
      setEaResults(annotated);
      if (annotated.length === 0) setEaError(t("onboarding.create.noEaResults"));
    } catch (e: any) {
      console.error("[onboarding ea search]", e);
      setEaError(e?.message || String(e));
    } finally {
      setEaSearching(false);
    }
  };

  return (
    <AuthShell wide>
      <Card className="bg-transparent animate-fade-in">
        <CardHeader className="p-0 pb-5 flex flex-col gap-3">
          {/* min-w-0 consente al testo di restringersi correttamente in un
              flex item, altrimenti email lunghe (a.camolese+test@gmail.com)
              forzerebbero overflow orizzontale su mobile. */}
          <div className="flex-1 min-w-0 space-y-1">
            <CardTitle className="text-[26px] font-bold leading-tight">{t("onboarding.title")}</CardTitle>
            <p className="text-sm text-muted-foreground break-words [overflow-wrap:anywhere]">
              <Trans
                i18nKey="onboarding.subtitle"
                t={t}
                values={{ email: userEmail }}
                components={{ em: <span className="font-medium" /> }}
              />
            </p>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs defaultValue="join">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="join">{t("onboarding.tabs.join")}</TabsTrigger>
              <TabsTrigger value="create">{t("onboarding.tabs.create")}</TabsTrigger>
            </TabsList>
            <TabsContent value="join" className="pt-4 space-y-3">
              {pendingRequest ? (
                <div className="space-y-3">
                  <div className="rounded-2xl border bg-amber-50 text-amber-800 p-4 space-y-2">
                    <p className="font-semibold text-sm">{t("onboarding.pending.title")}</p>
                    <p className="text-xs">{t("onboarding.pending.body")}</p>
                    <Button size="sm" variant="outline" onClick={cancelJoin} disabled={loading}>
                      {t("onboarding.pending.cancel")}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <Trans
                      i18nKey="onboarding.pending.alsoCreate"
                      t={t}
                      components={{ em: <span className="font-medium text-foreground" /> }}
                    />
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t("onboarding.join.intro")}
                  </p>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={joinQuery}
                      onChange={(e) => setJoinQuery(e.target.value)}
                      placeholder={t("onboarding.join.searchPlaceholder")}
                      className="pl-9"
                    />
                  </div>
                  <Input
                    value={joinNote}
                    onChange={(e) => setJoinNote(e.target.value)}
                    placeholder={t("onboarding.join.notePlaceholder")}
                  />
                  <div className="space-y-1.5 max-h-[260px] overflow-auto">
                    {joinResults.length === 0 && joinQuery.trim().length >= 2 && (
                      <p className="text-sm text-muted-foreground italic">{t("onboarding.join.noResults")}</p>
                    )}
                    {joinResults.length === 0 && joinQuery.trim().length < 2 && (
                      <p className="text-sm text-muted-foreground italic">
                        {t("onboarding.join.minChars")}
                      </p>
                    )}
                    {joinResults.map((tt) => (
                      <div
                        key={tt.id}
                        className="flex items-center gap-3 px-3 py-2 border rounded-md bg-card"
                      >
                        <TeamCrest team={tt} size={28} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{tt.name}</p>
                          <p className="text-xs text-muted-foreground font-mono truncate">{tt.slug}</p>
                        </div>
                        <Button size="sm" onClick={() => requestJoin(tt.id)} disabled={loading}>
                          {t("onboarding.join.request")}
                        </Button>
                      </div>
                    ))}
                  </div>
                  {error && (
                    <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 text-rose-700 border border-rose-200">
                      {error}
                    </div>
                  )}
                </>
              )}
            </TabsContent>
            <TabsContent value="create" className="pt-4">
              <p className="text-sm text-muted-foreground mb-4">
                <Trans
                  i18nKey="onboarding.create.intro"
                  t={t}
                  components={{ em: <span className="font-medium text-foreground" /> }}
                />
              </p>
              <form className="space-y-4" onSubmit={createTeam}>
                <div className="rounded-2xl bg-background p-3 space-y-2">
                  <p className="text-sm font-semibold flex items-center gap-1.5">
                    <FcIcon className="h-4 w-4" /> {t("onboarding.create.step1Title")}
                  </p>
                  {eaPicked ? (
                    <div className="flex items-center justify-between gap-2 px-2 py-2 rounded border bg-card">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{eaPicked.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {t("onboarding.create.eaLinked", {
                            clubId: eaPicked.clubId,
                            platform: eaPlatformLabel(eaPicked.platform),
                          })}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => {
                          setEaPicked(null);
                          setTeamName("");
                        }}
                      >
                        {t("onboarding.create.changeClub")}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-1.5">
                        <Input
                          value={eaQuery}
                          onChange={(e) => setEaQuery(e.target.value)}
                          placeholder={t("onboarding.create.eaSearchPlaceholder")}
                          className="h-8 text-sm flex-1"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              runEaSearchOnboarding();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          size="sm"
                          className="h-8"
                          onClick={runEaSearchOnboarding}
                          disabled={eaSearching || eaQuery.trim().length < 2}
                        >
                          {eaSearching ? t("onboarding.create.searching") : t("onboarding.create.search")}
                        </Button>
                      </div>
                      {eaError && (
                        <div className="text-xs text-rose-600">{eaError}</div>
                      )}
                      {eaResults.length > 0 && (
                        <div className="space-y-1 max-h-40 overflow-auto">
                          {eaResults.map((c) => (
                            <button
                              type="button"
                              key={`${c.platform}:${c.clubId}`}
                              disabled={c.alreadyTaken}
                              onClick={() => {
                                if (c.alreadyTaken) return;
                                setEaPicked({ clubId: c.clubId, name: c.name, platform: c.platform });
                                setEaResults([]);
                                setEaQuery("");
                                setTeamName(c.name);
                              }}
                              className={
                                c.alreadyTaken
                                  ? "w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded border bg-muted/30 text-sm cursor-not-allowed opacity-70"
                                  : "w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded border bg-card text-sm hover:bg-muted/40"
                              }
                            >
                              <div className="min-w-0 text-left">
                                <p className="font-medium truncate flex items-center gap-1.5">
                                  {c.name}
                                  <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                    {eaPlatformLabel(c.platform)}
                                  </span>
                                </p>
                                <p className="text-xs text-muted-foreground font-mono">
                                  {t("onboarding.create.clubInfo", {
                                    clubId: c.clubId,
                                    division: c.currentDivision != null ? ` · div ${c.currentDivision}` : "",
                                    games: c.gamesPlayed != null ? ` · ${c.gamesPlayed} pt` : "",
                                  })}
                                </p>
                                {c.alreadyTaken && (
                                  <p className="text-xs text-amber-700 mt-0.5">
                                    {t("onboarding.create.alreadyTakenLong")}
                                  </p>
                                )}
                              </div>
                              <span className={
                                c.alreadyTaken
                                  ? "text-xs text-muted-foreground"
                                  : "text-xs text-violet-700 font-semibold"
                              }>
                                {c.alreadyTaken ? t("onboarding.create.alreadyTaken") : t("onboarding.create.useClub")}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t("onboarding.create.step2Label")}</Label>
                  <Input
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder={eaPicked ? eaPicked.name : t("onboarding.create.teamNamePlaceholder")}
                    required
                  />
                  {!eaPicked && (
                    <p className="text-xs text-muted-foreground">
                      {t("onboarding.create.noEaHint")}
                    </p>
                  )}
                </div>

                {error && (
                  <div className="rounded-lg px-3 py-2 text-sm bg-rose-50 text-rose-700 border border-rose-200">
                    {error}
                  </div>
                )}
                {loading && loadingStep && (
                  <div className="rounded-lg px-3 py-2 text-sm bg-sky-50 text-sky-800 border border-sky-200 flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                    <span>{loadingStep}</span>
                  </div>
                )}
                <Button
                  variant="hero"
                  className="w-full"
                  disabled={loading || !teamName.trim()}
                  aria-busy={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" />
                      {t("onboarding.create.submitBusy")}
                    </>
                  ) : (
                    <>
                      <Plus />
                      {eaPicked ? t("onboarding.create.submitWithEa") : t("onboarding.create.submitIdle")}
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          <div className="pt-4 flex justify-end">
            <Button variant="ghost" onClick={onSignOut}>
              <LogOut />
              {t("onboarding.signOut")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
